import React, { FC, useState, useEffect, useRef } from "react";
import { 
  Card, 
  Button, 
  Space, 
  Form, 
  Divider,
  Typography,
  notification,
  Spin,
} from "antd";
import { 
  FileTextOutlined, 
  HomeOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../utils/functions';
import { CaseNumbersUrl, CaseDetailsUrl } from '../utils/network';
import axios from 'axios';
import { getCaseSections } from '../utils/caseSections';

// Import all section components
import GeneralInformation from '../components/caseSections/GeneralInformation';
import Handicap from '../components/caseSections/Handicap';
import Accommodation from '../components/caseSections/Accommodation';
import Age from '../components/caseSections/Age';
import Issues from '../components/caseSections/Issues';
import Losses from '../components/caseSections/Losses';
import Harassment from '../components/caseSections/Harassment';
import PositionInformation from '../components/caseSections/PositionInformation';
import SelecteesInformation from '../components/caseSections/SelecteesInformation';
import SexualHarassment from '../components/caseSections/SexualHarassment';
import Termination from '../components/caseSections/Termination';
import CoworkersInformation from '../components/caseSections/CoworkersInformation';
import Responses from '../components/caseSections/Responses';
import AnalysisResultsModal from '../components/AnalysisResultsModal';

const { Title } = Typography;

const CaseCreation: FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [caseType, setCaseType] = useState<string | undefined>();
  const [discriminationBasis, setDiscriminationBasis] = useState<string | undefined>();
  const [sections, setSections] = useState(getCaseSections(undefined, undefined));
  const [nextCaseNo, setNextCaseNo] = useState<number | null>(null);
  const [fetchingCaseNo, setFetchingCaseNo] = useState(true);
  const [createdCaseNumberId, setCreatedCaseNumberId] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Analysis results modal state
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [caseResponse, setCaseResponse] = useState<any>(null);

  // Update sections when case type or basis changes
  useEffect(() => {
    const newSections = getCaseSections(caseType, discriminationBasis);
    setSections(newSections);
  }, [caseType, discriminationBasis]);

  // Fetch next case number on component mount
  useEffect(() => {
    const fetchNextCaseNumber = async () => {
      try {
        const headers = getAuthToken();
        if (!headers) {
          return;
        }
        const response = await axios.get(`${CaseNumbersUrl}next/`, headers);
        const nextNo = response.data.next_case_no;
        setNextCaseNo(nextNo);
        form.setFieldsValue({ case_no: nextNo });
      } catch (error) {
        console.error('Error fetching next case number:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to fetch next case number. Please refresh the page.',
        });
      } finally {
        setFetchingCaseNo(false);
      }
    };

    fetchNextCaseNumber();
  }, [form]);

  // Cleanup: Delete case number if component unmounts without successful submission
  // Use ref to capture latest values without causing re-runs
  const createdCaseNumberIdRef = useRef<number | null>(null);
  const isSubmittedRef = useRef<boolean>(false);
  
  useEffect(() => {
    createdCaseNumberIdRef.current = createdCaseNumberId;
  }, [createdCaseNumberId]);
  
  useEffect(() => {
    isSubmittedRef.current = isSubmitted;
  }, [isSubmitted]);
  
  useEffect(() => {
    return () => {
      // Cleanup on unmount only - delete case number if it was created but not submitted
      if (createdCaseNumberIdRef.current && !isSubmittedRef.current) {
        const cleanup = async () => {
          try {
            const headers = getAuthToken();
            if (headers) {
              await axios.delete(`${CaseNumbersUrl}${createdCaseNumberIdRef.current}/`, headers);
              console.log(`Cleaned up case number ${createdCaseNumberIdRef.current}`);
            }
          } catch (error) {
            console.error('Error cleaning up case number:', error);
          }
        };
        cleanup();
      }
    };
  }, []); // Empty dependency array - only run on unmount

  // Watch form values for case type and discrimination basis
  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.case_type !== undefined) {
      setCaseType(changedValues.case_type);
    }
    if (changedValues.discrimination_basis !== undefined) {
      setDiscriminationBasis(changedValues.discrimination_basis);
    }
  };

  const onSubmit = async (values: any) => {
    setLoading(true);

    try {
      const headers = getAuthToken();
      if (!headers) {
        throw new Error('Authentication required');
      }

      // Step 1: Create case number (auto-generated by backend)
      let caseNumberId;
      let generatedCaseNo: number;
      
      try {
        // Create new case number (backend will auto-generate the number)
        const createCaseNumberResponse = await axios.post(
          CaseNumbersUrl,
          {}, // No case_no needed, backend will auto-generate
          headers
        );
        caseNumberId = createCaseNumberResponse.data.id;
        generatedCaseNo = createCaseNumberResponse.data.case_no;
        
        // Track created case number ID for cleanup if submission fails
        setCreatedCaseNumberId(caseNumberId);
        
        // Update form with the generated case number
        form.setFieldsValue({ case_no: generatedCaseNo });
        
        // Fetch next case number for future use
        const nextResponse = await axios.get(`${CaseNumbersUrl}next/`, headers);
        setNextCaseNo(nextResponse.data.next_case_no);
      } catch (error: any) {
        throw new Error(`Failed to create case number: ${error.response?.data?.detail || error.message}`);
      }

      // Step 2: Prepare case details data with all nested models
      const caseDetailsData: any = {
        case_number: caseNumberId,
        case_type: values.case_type,
        discrimination_basis: values.discrimination_basis,
        date_discrimination_occurred: values.date_discrimination_occurred,
        date_of_interview: values.date_of_interview,
        last_name: values.last_name,
        first_name: values.first_name,
        middle: values.middle,
        company_name: values.company_name,
        accused_last_name: values.accused_last_name,
        accused_first_name: values.accused_first_name,
      };

      // Add basis-specific fields
      if (values.your_age) caseDetailsData.your_age = values.your_age;
      if (values.your_color) caseDetailsData.your_color = values.your_color;
      if (values.your_handicap) caseDetailsData.your_handicap = values.your_handicap;
      if (values.your_national_origin) caseDetailsData.your_national_origin = values.your_national_origin;
      if (values.your_race) caseDetailsData.your_race = values.your_race;
      if (values.your_religion) caseDetailsData.your_religion = values.your_religion;
      if (values.your_reprisal) caseDetailsData.your_reprisal = values.your_reprisal;
      if (values.your_sex) caseDetailsData.your_sex = values.your_sex;

      if (values.accused_age) caseDetailsData.accused_age = values.accused_age;
      if (values.accused_color) caseDetailsData.accused_color = values.accused_color;
      if (values.accused_handicap) caseDetailsData.accused_handicap = values.accused_handicap;
      if (values.accused_national_origin) caseDetailsData.accused_national_origin = values.accused_national_origin;
      if (values.accused_race) caseDetailsData.accused_race = values.accused_race;
      if (values.accused_religion) caseDetailsData.accused_religion = values.accused_religion;
      if (values.accused_reprisal) caseDetailsData.accused_reprisal = values.accused_reprisal;
      if (values.accused_sex) caseDetailsData.accused_sex = values.accused_sex;

      // Add nested models based on sections shown
      if (sections.showHandicap && values.handicap_info) {
        caseDetailsData.handicap_info = values.handicap_info;
      }

      if (sections.showAccommodation && values.accommodation_info) {
        caseDetailsData.accommodation_info = values.accommodation_info;
      }

      if (sections.showAge && values.age_info) {
        caseDetailsData.age_info = values.age_info;
      }

      if (sections.showIssues && values.issues_info) {
        caseDetailsData.issues_info = values.issues_info;
      }

      if (sections.showLosses && values.losses_info) {
        caseDetailsData.losses_info = values.losses_info;
      }

      if (sections.showHarassment && values.harassment_info) {
        caseDetailsData.harassment_info = values.harassment_info;
      }

      if (sections.showPositionInformation && values.position_info) {
        caseDetailsData.position_info = values.position_info;
      }

      if (sections.showSelecteesInformation && values.selectees_info) {
        caseDetailsData.selectees_info = values.selectees_info;
      }

      if (sections.showSexualHarassment && values.sexual_harassment_info) {
        caseDetailsData.sexual_harassment_info = values.sexual_harassment_info;
      }

      if (sections.showTermination && values.termination_info) {
        caseDetailsData.termination_info = values.termination_info;
      }

      if (sections.showCoworkersInformation && values.coworkers_info) {
        caseDetailsData.coworkers_info = values.coworkers_info;
      }

      if (sections.showResponses && values.responses_info) {
        caseDetailsData.responses_info = values.responses_info;
      }

      // Step 3: Create case details with all nested models in a single request
      const response = await axios.post(
        CaseDetailsUrl,
        caseDetailsData,
        headers
      );

      // Mark as submitted IMMEDIATELY after successful creation
      setIsSubmitted(true);
      // Clear the case number ID since case was successfully created
      // (case number is now linked to case details and shouldn't be deleted)
      setCreatedCaseNumberId(null);

      // Store response data for results modal
      setCaseResponse(response.data);

      // Show results modal with PDF download option
      setShowResultsModal(true);
      
    } catch (error: any) {
      // Cleanup: Delete case number ONLY if case details creation failed
      if (createdCaseNumberId) {
        try {
          const headers = getAuthToken();
          if (headers) {
            await axios.delete(`${CaseNumbersUrl}${createdCaseNumberId}/`, headers);
            console.log(`Cleaned up case number ${createdCaseNumberId} due to submission failure`);
            setCreatedCaseNumberId(null);
          }
        } catch (cleanupError) {
          console.error('Error cleaning up case number:', cleanupError);
        }
      }

      let errorMessage = "Submission Failed";
      let errorDescription = "We encountered an issue while processing your case submission.";
      
      if (error.response?.data) {
        if (error.response.data.case_no) {
          errorDescription = `Case number ${form.getFieldValue('case_no')} already exists. Please use a different case number.`;
        } else if (error.response.data.error) {
          errorDescription = error.response.data.error;
        } else if (error.response.data.message) {
          errorDescription = error.response.data.message;
        } else if (typeof error.response.data === 'object') {
          errorDescription = JSON.stringify(error.response.data);
        }
      }

      notification.error({
        message: errorMessage,
        description: errorDescription,
        duration: 6,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin 
      spinning={loading} 
      tip="Creating case..." 
      size="large"
      indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
    >
      <div style={{ padding: '20px 50px' }}>
        {/* Header Card */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                <span style={{ fontSize: '24px', fontWeight: 600, color: '#000' }}>
                  {fetchingCaseNo ? 'Loading...' : `Case Number - ${nextCaseNo || 'N/A'}`}
                </span>
              </div>
              <Space>
              <Button 
                icon={<HomeOutlined />} 
                onClick={async () => {
                  // Cleanup: Delete case number if user navigates away
                  if (createdCaseNumberId && !isSubmitted) {
                    try {
                      const headers = getAuthToken();
                      if (headers) {
                        await axios.delete(`${CaseNumbersUrl}${createdCaseNumberId}/`, headers);
                        console.log(`Cleaned up case number ${createdCaseNumberId} due to navigation`);
                      }
                    } catch (error) {
                      console.error('Error cleaning up case number:', error);
                    }
                  }
                  navigate('/');
                }}
                type="default"
              >
                Home
              </Button>
              </Space>
            </div>
          </div>
        </Card>

        {/* Case Creation Form */}
        <Form 
          className="case-creation-form"
          layout="vertical" 
          onFinish={onSubmit} 
          form={form}
          onValuesChange={handleFormValuesChange}
          scrollToFirstError
        >
          {/* General Information - Always shown */}
          {sections.showGeneralInformation && (
            <GeneralInformation 
              form={form} 
              discriminationBasis={discriminationBasis} 
              caseNo={nextCaseNo}
              loadingCaseNo={fetchingCaseNo}
            />
          )}

          {/* Conditional Sections */}
          {sections.showHandicap && <Handicap form={form} />}
          {sections.showAccommodation && <Accommodation form={form} />}
          {sections.showAge && <Age form={form} />}
          {sections.showIssues && <Issues form={form} />}
          {sections.showHarassment && <Harassment form={form} />}
          {sections.showPositionInformation && <PositionInformation form={form} />}
          {sections.showSelecteesInformation && <SelecteesInformation form={form} />}
          {sections.showLosses && <Losses form={form} caseType={caseType} discriminationBasis={discriminationBasis} />}
          {sections.showSexualHarassment && <SexualHarassment form={form} />}
          {sections.showTermination && <Termination form={form} />}
          {sections.showCoworkersInformation && <CoworkersInformation form={form} />}
          {sections.showResponses && <Responses form={form} />}

          <Divider />

          {/* Submit Button */}
          <Card>
            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button 
                  onClick={async () => {
                    // Cleanup: Delete case number if user cancels
                    if (createdCaseNumberId && !isSubmitted) {
                      try {
                        const headers = getAuthToken();
                        if (headers) {
                          await axios.delete(`${CaseNumbersUrl}${createdCaseNumberId}/`, headers);
                          console.log(`Cleaned up case number ${createdCaseNumberId} due to cancellation`);
                        }
                      } catch (error) {
                        console.error('Error cleaning up case number:', error);
                      }
                    }
                    navigate('/');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  htmlType="submit" 
                  type="primary" 
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                >
                  Finish
                </Button>
              </Space>
            </Form.Item>
          </Card>
        </Form>
      </div>
      
      {/* Analysis Results Modal */}
      {caseResponse && (
        <AnalysisResultsModal
          visible={showResultsModal}
          onClose={() => {
            setShowResultsModal(false);
            // Reset form
            form.resetFields();
            setCaseType(undefined);
            setDiscriminationBasis(undefined);
            setSections(getCaseSections(undefined, undefined));
            // Clear case number ID AFTER successful submission (don't reset isSubmitted - case was successfully created)
            setCreatedCaseNumberId(null);
            setCaseResponse(null);
            
            // Update form with new next case number
            form.setFieldsValue({ case_no: nextCaseNo });
            
            // Reset submitted flag only when starting fresh (not after successful submission)
            // This will be reset when user starts creating a new case
            
            // Navigate back to dashboard
            navigate('/');
          }}
          analysisResults={{
            likelihood: (caseResponse.likelihood || 'Medium') as 'High' | 'Moderate' | 'Low',
            confidence_score: caseResponse.weighted_score ? parseFloat(caseResponse.weighted_score) / 5 : 0.4,
            case_ref: `Case #${caseResponse.case_no || caseResponse.case_number_detail?.case_no || 'N/A'}`,
            reasoning: `Based on the analysis, this case has a ${caseResponse.likelihood || 'Medium'} likelihood index with a weighted score of ${caseResponse.weighted_score || 'N/A'}.`
          }}
          caseNumber={caseResponse.case_no || caseResponse.case_number_detail?.case_no || 0}
          pdfUrl={caseResponse.pdf_url}
          pdfFilename={caseResponse.pdf_filename}
        />
      )}
    </Spin>
  );
};

export default CaseCreation;
